/**
 * AccountSetupModal.js — First-login account setup
 *
 * Shown immediately after a staff member logs in with a temporary password.
 * Requires: new password, phone, date of birth, address, emergency contact.
 * On completion: calls updatePortalAccount (which sets isTemporaryPassword: false,
 * accountSetupComplete: true), then navigates to /portal.
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PORTAL_SESSION_KEY } from '../AccountModal';
import { formatPhone } from '../../utilities/formatPhone';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';

const functions = getFunctions();
const updatePortalAccount = httpsCallable(functions, 'updatePortalAccount');

const STEPS = ['Password', 'Personal Info', 'Emergency Contact', 'POS PIN'];

const AccountSetupModal = ({ user, onComplete }) => {
  const [step, setStep]       = useState(0);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useBodyScrollLock();

  // Step 0 — password
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 1 — personal info
  const [phone, setPhone]     = useState('');
  const [dob, setDob]         = useState('');
  const [street, setStreet]   = useState('');
  const [city, setCity]       = useState('');
  const [state, setState]     = useState('');
  const [zip, setZip]         = useState('');

  // Step 2 — emergency contact
  const [ecName, setEcName]           = useState('');
  const [ecRel, setEcRel]             = useState('');
  const [ecPhone, setEcPhone]         = useState('');

  // Step 3 — POS PIN
  const [posPin, setPosPin]           = useState('');
  const [posPinConfirm, setPosPinConfirm] = useState('');

  const handleNext = () => {
    setError('');
    if (step === 0) {
      if (!newPassword) return setError('Please enter a new password.');
      if (newPassword.length < 8) return setError('Password must be at least 8 characters.');
      if (!confirmPassword) return setError('Please confirm your password.');
      if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    }
    if (step === 1) {
      if (!phone || phone.length < 12) return setError('Please enter a valid 10-digit phone number.');
      if (!dob) return setError('Please enter your date of birth.');
      if (!street.trim()) return setError('Please enter your street address.');
      if (!city.trim()) return setError('Please enter your city.');
      if (!state.trim() || state.trim().length < 2) return setError('Please enter a valid 2-letter state.');
      if (!zip.trim() || zip.trim().length < 5) return setError('Please enter a valid 5-digit ZIP code.');
    }
    if (step === 2) {
      if (!ecName.trim()) return setError('Please enter your emergency contact\'s name.');
      if (!ecRel.trim()) return setError('Please enter your relationship to the contact.');
      if (!ecPhone || ecPhone.length < 12) return setError('Please enter a valid 10-digit phone number for your emergency contact.');
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setError('');
    if (!/^\d{4}$/.test(posPin)) return setError('Please enter a 4-digit PIN.');
    if (posPin !== posPinConfirm) return setError('PINs do not match.');
    setSaving(true);
    try {
      await updatePortalAccount({
        accountId: user.accountId,
        newPassword,
        updates: {
          phone,
          dateOfBirth: dob || null,
          address: { street, city, state, zip },
          emergencyContact: { name: ecName, relationship: ecRel, phone: ecPhone },
          posPin,
        },
      });

      // Update the stored session so the portal reflects the completed state
      const stored = JSON.parse(localStorage.getItem(PORTAL_SESSION_KEY) || '{}');
      localStorage.setItem(PORTAL_SESSION_KEY, JSON.stringify({
        ...stored,
        phone,
        dateOfBirth: dob || null,
        address: { street, city, state, zip },
        emergencyContact: { name: ecName, relationship: ecRel, phone: ecPhone },
        posPin,
        isTemporaryPassword: false,
        accountSetupComplete: true,
      }));

      onComplete();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 px-4 py-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="bg-neutral-900 px-8 pt-8 pb-6 text-center shrink-0">
          <p className="text-sm text-neutral-400 uppercase tracking-widest mb-1">Welcome to GoHost</p>
          <h2 className="text-lg font-bold text-white">Complete Your Account Setup</h2>
          {/* Step indicators */}
          <div className="flex justify-center gap-2 mt-5">
            {STEPS.map((label, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  i < step ? 'bg-emerald-500 text-white' :
                  i === step ? 'bg-white text-neutral-900' :
                  'bg-neutral-700 text-neutral-400'
                }`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span className={`text-sm ${i === step ? 'text-white' : 'text-neutral-500'}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Form body */}
        <div className="bg-white px-8 py-7 space-y-4 overflow-y-auto flex-1">

          {/* ── Step 0: Password ── */}
          {step === 0 && (
            <>
              <p className="text-sm text-neutral-500">Create a secure password for your account. You will use this every time you sign in.</p>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>
            </>
          )}

          {/* ── Step 1: Personal Info ── */}
          {step === 1 && (
            <>
              <p className="text-sm text-neutral-500">Add your contact and address details. These are used for payroll and scheduling.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(formatPhone(e.target.value))}
                    placeholder="___-___-____"
                    maxLength={12}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    autoFocus
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={dob}
                    onChange={e => setDob(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-neutral-500 mb-1">Street Address</label>
                  <input
                    type="text"
                    value={street}
                    onChange={e => setStreet(e.target.value)}
                    placeholder="123 Main St"
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-500 mb-1">City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={e => setCity(e.target.value)}
                    placeholder="City"
                    className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">State</label>
                    <input
                      type="text"
                      value={state}
                      onChange={e => setState(e.target.value)}
                      placeholder="TX"
                      maxLength={2}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500 mb-1">ZIP</label>
                    <input
                      type="text"
                      value={zip}
                      onChange={e => setZip(e.target.value)}
                      placeholder="75001"
                      maxLength={5}
                      className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Emergency Contact ── */}
          {step === 2 && (
            <>
              <p className="text-sm text-neutral-500">Add an emergency contact. This information is kept confidential and only used when necessary.</p>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={ecName}
                  onChange={e => setEcName(e.target.value)}
                  placeholder="Full name"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Relationship</label>
                <input
                  type="text"
                  value={ecRel}
                  onChange={e => setEcRel(e.target.value)}
                  placeholder="e.g. Spouse, Parent, Sibling"
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Phone</label>
                <input
                  type="tel"
                  value={ecPhone}
                  onChange={e => setEcPhone(formatPhone(e.target.value))}
                  placeholder="___-___-____"
                  maxLength={12}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>
            </>
          )}

          {/* ── Step 3: POS PIN ── */}
          {step === 3 && (
            <>
              <p className="text-sm text-neutral-500">Create a 4-digit PIN for clocking in and out on the POS system. You'll enter this PIN at the terminal instead of a password.</p>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">POS PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4}"
                  value={posPin}
                  onChange={e => setPosPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="4-digit PIN"
                  maxLength={4}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 tracking-widest text-center text-lg"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500 mb-1">Confirm PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4}"
                  value={posPinConfirm}
                  onChange={e => setPosPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="Re-enter PIN"
                  maxLength={4}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 tracking-widest text-center text-lg"
                />
              </div>
            </>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white px-8 pb-7 pt-3 border-t border-neutral-100 flex items-center justify-between shrink-0">
          {step > 0 ? (
            <button
              onClick={() => { setError(''); setStep(s => s - 1); }}
              className="text-sm text-neutral-500 hover:text-neutral-800 transition-colors"
            >
              ← Back
            </button>
          ) : (
            <span />
          )}

          {step < STEPS.length - 1 ? (
            <button
              onClick={handleNext}
              className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Enter Portal →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSetupModal;
